import React, { useState, useEffect } from "react";
import {
  FaTrash,
  FaEdit,
  FaInbox,
  FaFileDownload,
  FaFileInvoiceDollar,
} from "react-icons/fa";
import { HiOutlineTruck, HiOutlineCube } from "react-icons/hi";
import { MdDirectionsCar, MdPedalBike } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { Circles } from "react-loader-spinner";
import {
  getMyLead,
  getMyLeadByCategory,
  deleteLead,
} from "../redux/action/request";

const Lead = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requestSent");
  const [searchTerm, setSearchTerm] = useState("");
  const { leads } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getMyLeadByCategory("requestSent"));
    const timer = setTimeout(() => setLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const getMyLeads = () => {
    const userId = localStorage.getItem("userId");
    dispatch(getMyLead(userId));
  };

  const handleDelete = (lead) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      dispatch(deleteLead(lead._id));
      setTimeout(() => {
        getMyLeads();
      }, 500);
    }
  };

  const goToTab = (tab, actionType) => {
    setLoading(true);
    setActiveTab(tab);
    dispatch(getMyLeadByCategory(actionType));
    setTimeout(() => setLoading(false), 500);
  };

  const getVehicleIcon = (vehicleType) => {
    if (!vehicleType) return <HiOutlineCube className="text-gray-400" />;

    const icons = {
      car: <MdDirectionsCar className="text-blue-500" />,
      bike: <MdPedalBike className="text-green-500" />,
      "commercial vehicle": <HiOutlineTruck className="text-orange-500" />,
    };

    return (
      icons[vehicleType.toLowerCase()] || (
        <HiOutlineCube className="text-gray-400" />
      )
    );
  };

  const getDaysAgo = (date) => {
    if (!date) return "Recently";
    const diff = Date.now() - new Date(date);
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return days === 0 ? "Today" : `${days} day${days > 1 ? "s" : ""} ago`;
  };

  const filteredLeads = leads.filter(
    (lead) =>
      lead.registerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.ownerDetail?.ownerName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (lead) => {
    if (lead.policyUploaded) {
      return (
        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
          Policy Issued
        </span>
      );
    }
    if (lead.paymentDone) {
      return (
        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
          Payment Done
        </span>
      );
    }
    if (lead.quotationMade) {
      return (
        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
          Quoted
        </span>
      );
    }
    return (
      <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
        Request Sent
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          
          <p className="mt-1 text-sm text-gray-500">
            Track and manage all your insurance requests in one place
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 border-b border-gray-200">
            <div className="flex space-x-2 mb-3 sm:mb-0 w-full sm:w-auto">
              <button
                onClick={() => goToTab("requestSent", "requestSent")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  activeTab === "requestSent"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Request Sent
              </button>
              <button
                onClick={() => goToTab("quotation", "quotationMade")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  activeTab === "quotation"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Quotation Made
              </button>
              <button
                onClick={() => goToTab("payment", "paymentDone")}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  activeTab === "payment"
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Payment Done
              </button>
            </div>

            <div className="relative w-full sm:w-64">
              <input
                type="text"
                placeholder="Search leads..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute left-3 top-2.5 text-gray-400">
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Circles
                height="40"
                width="40"
                color="#3B82F6"
                ariaLabel="loading"
              />
            </div>
          ) : filteredLeads.length <= 0 ? (
            <div className="h-64 flex flex-col justify-center items-center text-gray-400">
              <FaInbox size={48} className="mb-4" />
              <p className="text-lg font-medium text-gray-500">
                No leads found
              </p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try a different search term"
                  : "Get started by creating a new request"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredLeads.map((lead) => (
                <div
                  key={lead._id}
                  className={`p-4 hover:bg-gray-50 transition-colors duration-150 ${
                    lead?.comments?.length ? "bg-red-50" : ""
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 pt-1 pr-3">
                      <div className="text-2xl">
                        {getVehicleIcon(lead?.vehicleType)}
                      </div>
                    </div>

                    <div
                      className="flex-grow cursor-pointer"
                      onClick={() =>
                        navigate(`/request-detail/${lead._id}`,
                          { state: lead }
                        )
                      }
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {lead?.registerNumber || "Unknown Vehicle"}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {lead?.make} • {lead?.model || "N/A"} •{" "}
                            {lead?.variant || "N/A"}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(lead)}
                          {lead?.comments?.length > 0 && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full">
                              Attention Needed
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="mt-2 flex flex-wrap items-center text-sm text-gray-500 space-x-4">
                        {lead?.ownerDetail?.ownerName && (
                          <span className="flex items-center">
                            <svg
                              className="h-4 w-4 mr-1 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {lead.ownerDetail.ownerName}
                          </span>
                        )}

                        {lead?.registerDate && (
                          <span className="flex items-center">
                            <svg
                              className="h-4 w-4 mr-1 text-gray-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                                clipRule="evenodd"
                              />
                            </svg>
                            {getDaysAgo(lead.registerDate)}
                          </span>
                        )}
                      </div>

                      {lead?.message && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                          {lead.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex justify-between items-center">
                    <div>
                      {lead?.policyUploaded ? (
                        <button className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
                          <FaFileDownload className="mr-2" />
                          Download Policy
                        </button>
                      ) : (
                        lead?.quotationMade &&
                        !lead?.paymentDone && (
                          <button
                            className="inline-flex items-center text-sm font-medium text-green-600 hover:text-green-800"
                            
                          >
                            <FaFileInvoiceDollar className="mr-2" />
                            Pay For Quotation
                          </button>
                        )
                      )}
                    </div>

                    <div className="flex space-x-3">
                      {activeTab === "requestSent" && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/quotation", { state: lead });
                            }}
                            className="text-gray-500 hover:text-blue-600 p-1 rounded-full hover:bg-blue-50"
                            title="Edit"
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(lead);
                            }}
                            className="text-gray-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50"
                            title="Delete"
                          >
                            <FaTrash size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {lead?.comments?.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 rounded-md border border-red-100">
                      <p className="text-sm font-medium text-red-700">
                        {lead.comments[0].discription}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Lead;
